import { defineStore } from 'pinia'
import { apiClient } from '@/helpers/api.js'
import { handleApiError } from '@/services/errorHandler'
import { sortSubsetSuggestions } from "@/helpers/batch.js"
import { 
  GRADED,
  ALL_CONDITIONS,
  BULK_FIELDS_OBJECT,
  TCG_CONDITION,
  CONDITION,
  BULK_PRICING_CONFIGS,
  CALCULATION_TYPES,
  CALCULATION_METHODS,
  ROUNDING_OPTIONS
} from '@/constants'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBatchStore } from '@/stores/BatchStore'
import { useUserStore } from '@/stores/UserStore'
import { useInventoryStore } from '@/stores/InventoryStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { generateCardData, getProperSport, getUniqueCardTypes, getBulkSalesValues } from "@/helpers/card.js"
import { gridApi } from '@/hooks/gridApiRef'

export const useCardStore = defineStore('CardStore', {
  state: () => ({
    cardsLoading: false,
    batchImageCellsLoading: false,
    loadingSuggestions: false,

    selectedCardData: {},

    graded: [...GRADED],
    grader: [],

    cards: [],
    duplicates:  [],

    hasDuplicates: false,
    duplicationStep: 1,
    duplicationPopup: false,
    duplicatesLength: 0,

    selectedCardId: null,

    /**
     * Where "Collapse to panel" on the item-details page sends the user back to
     * — the full path they opened the item FROM (an inventory grid, a batch, a
     * filtered view). Written by whatever navigates to `/inventory/:id`; the
     * details page consumes and clears it. Empty = arrived directly, fall back
     * to `/inventory`.
     */
    panelReturnTo: '',

    pendingDatabaseSearchCardId: null,

    cardIndex: 1,
    totalCards: 0,

    pinnedCardData: {},

    swappedToast: false,
    swappedToastMessage: '',

    selectedRanges: null,
    selectedCards: {},
    selectedColumn: '',
    shouldUpdateColumn: false,

    fieldSearchResult: [],

    automaticallyGenerateSku: true,

    deleteCardSuccess: false,
    deleteCardError: false,

    fieldSuggestions: [],

    itemFilters: {},
    availableFilters: {},

    totalCards: 0,
    pagination: null,
    page: 1,

    searchActive: false,
    searchQuery: '',

    openFullScreenImage: false,
    imageForFullScreen: null,

    isBulkEditing: false,
    currentBulkValue: {},
    reopenBulkEditingInfo: {
      open: false,
    },

    isTryToExport: false,

    resetProcessingStarted: false,

    bulkPricingConfigs: { ...BULK_PRICING_CONFIGS },

    bulkSalesValues: [],
    
    anotherPriceSource: false,
  }),

  getters: {
    // selectedCardData() {
    //   return this.cards.find(item => item.id === Number(this.selectedCardId)) || {}
    // },

    condition() {
      const batchStore = useBatchStore()

      if (batchStore.selectedBatch?.type) {
        return ALL_CONDITIONS[batchStore.selectedBatch?.type]
      }

      const types = getUniqueCardTypes(this.selectedCards)

      // Only return conditions if all cards have the same type
      if (types.length === 1) {
        return ALL_CONDITIONS[types[0]]
      }
      
      // If more than one type, return empty array
      return []
    },

    conditionBasedOnBatchType() {
      return this.selectedCardData?.type === 'tcg' ? [...TCG_CONDITION] : [...CONDITION]
    },

    grade_number() {
      const configStore = useConfigStore()
      let grade = []

      if (!this.selectedCardData) return grade

      if (Object.keys(this.selectedCardData).length) {
        const grader = configStore.config.graders?.find(item => item.name === this.selectedCardData?.grader)

        if (!grader) return grade

        grade = grader.grading_scale.reduce((acc, item) => {
          acc.push({ name: item, id: item })

          return acc
        }, [])
      }

      return grade
    },

    sport() {
      const configStore = useConfigStore()
      const batchStore = useBatchStore()

      let type = batchStore.selectedBatch?.type || 'sports'

      if (type === 'tcg') {
        type = 'games'
      }

      return configStore.config ? configStore.config[type] : []
    },
   
    attributes() {
      return this.selectedCardData.attributes ? this.selectedCardData.attributes.split(',') : []
    },

    cardErrors() {
      const batchStore = useBatchStore()

      const attributes = []
      const grading = []
      const pricing = []
      const templates = []
      const images = []

      if (Object.keys(this.selectedCardData).length) {
        for (const [key, value] of Object.entries(this.selectedCardData?.errors || {})) {
          // Skip any error key without a field mapping (e.g. a bare `grade`) so a stray key can't throw
          // and take down the whole error list.
          if (!BULK_FIELDS_OBJECT[key]) continue
          if (['front_image'].includes(key) && batchStore.selectedBatch.type === 'sports') {
            images.push({ text: BULK_FIELDS_OBJECT[key].name, el: `inspector-${BULK_FIELDS_OBJECT[key].field}-el` })
          }
          if (['year', 'type', 'sport', 'brand', 'set', 'card_number', 'player', 'subset', 'variation', 'attributes', 'team', 'number'].includes(key) && batchStore.selectedBatch.type === 'sports') {
            attributes.push({ text: BULK_FIELDS_OBJECT[key].name, el: `inspector-${BULK_FIELDS_OBJECT[key].field}-el`, desc: '' })
          }
          if (['type', 'sport', 'brand', 'set', 'card_number', 'player', 'variation', 'attributes', 'number'].includes(key) && batchStore.selectedBatch.type === 'tcg') {
            attributes.push({ text: BULK_FIELDS_OBJECT[key].name, el: `inspector-${BULK_FIELDS_OBJECT[key].field}-el`, desc: '' })
          }
          if (['condition', 'grade', 'grader', 'cert_no', 'grade_number'].includes(key)) {
            grading.push({ text: BULK_FIELDS_OBJECT[key].name, el: `inspector-${BULK_FIELDS_OBJECT[key].field}-el`, desc: '' })
          }
          if (['start_price', 'purchase_price', 'sku', 'cabinet', 'shelf', 'box', 'row', 'section'].includes(key)) {
            pricing.push({ text: BULK_FIELDS_OBJECT[key].name, el: `inspector-${BULK_FIELDS_OBJECT[key].field}-el`, desc: '' })
          }
          if (['title', 'description', 'title_template'].includes(key)) {
            const key_ = key === 'title_template' ? 'title' : key
            templates.push({ text: BULK_FIELDS_OBJECT[key].name, el: `inspector-${BULK_FIELDS_OBJECT[key_].field}-el`, desc: this.selectedCardData.errors[BULK_FIELDS_OBJECT[key_].field] })
          }
        }
      }

      return {
        attributes,
        grading,
        pricing,
        templates,
        images
      }
    },

    batchCardsBasedOnStatus() {
      const draftBatchCards = []
      const readyToListBatchCards = []
      const processingBatchCards = []

      for(const item of this.cards) {
        item.status === 'draft' && draftBatchCards.push(item)
        item.status === 'ready_to_list' && readyToListBatchCards.push(item)
        item.status === 'processing' && processingBatchCards.push(item)
      }
      return {
        draftBatchCards,
        readyToListBatchCards,
        processingBatchCards
      }
    },
  },

  actions: {
    setCardStoreFields(fields) {
      for (const [field, value] of Object.entries(fields)) {
        this[field] = value
      }
    },

    updateSwappedToast(value, message = '') {
      this.swappedToast = value
      this.swappedToastMessage = message

      setTimeout(() => {
        this.swappedToast = false
        this.swappedToastMessage = ''
      }, 4000)
    },

    /**
     * Move the selection by id.
     *
     * Also moves `selectedCardData`, because the Inspector / preview panel
     * renders THAT, not the id. On the batch routes the two stayed in step by
     * accident — the URL change refetched the details — but Inventory's panel
     * has no route behind it, so selection moved and the panel went on showing
     * the previous card.
     *
     * Only overwritten when the row is actually on this page: callers that fetch
     * a card's full details separately must not be blanked by a lookup miss.
     */
    updatedSelectedCard(id) {
      this.selectedCardId = id

      const row = this.cards.find(item => String(item.id) === String(id))

      if (row) this.selectedCardData = row
    },

    async fetchSelectedCards(query, turnOnLoader = true) {
      const userStore = useUserStore()
      const globalStore = useGlobalStore()
      const configStore = useConfigStore()
      const batchStore = useBatchStore()
      if (turnOnLoader) {
        this.cardsLoading = true
      }

      let _query = { ...query }

      if (!_query.sortBy) {
        const savedBatchDetailsSort = JSON.parse(localStorage.getItem('batchDetailsSort'))

        _query = { ..._query, sortBy: savedBatchDetailsSort && savedBatchDetailsSort[userStore.user?.id] && savedBatchDetailsSort[userStore.user?.id].value || 'filename' }
      }

      await configStore.fetchConfigs()

      batchStore.selectedBatchName = null

      try {
        const response = await apiClient().get(`/batches/${batchStore.selectedBatchId}/cards`, _query ? {
          params: _query
        } : {})

        const data = response.data

        batchStore.selectedBatchName = data.batch_name

        const cards = []
        data.items.forEach((card, index) => {
          const cardData = generateCardData(card, this.selectedRanges, configStore.config)
          cards.push(cardData)
        })

        this.cards = cards

        // Load market-price recommendations for this page of batch cards, using
        // the SAME query so prices line up with the current filters/pagination.
        // Fire-and-forget: it merges into `cards` when it resolves (like Inventory).
        batchStore.fetchMarketPrices({ batchId: batchStore.selectedBatchId, query: _query })

        globalStore.cellsLoading = false
        this.batchImageCellsLoading = false
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'fetchSelectedCards' })
      } finally {
        this.cardsLoading = false
        globalStore.cellsLoading = false
        this.batchImageCellsLoading = false
      }
    },

    async updateFields(cardId, body) {
      const configStore = useConfigStore()

      try {
        if (!configStore.config?.hasActiveSubscription) return

        const { data } = await apiClient().patch(`/cards/${cardId}`, body)

        const card = this.cards.find(element => element.id === cardId)

        const getFocusedCell = gridApi.value?.getFocusedCell()
        const getRowNode = getFocusedCell ? gridApi.value?.getDisplayedRowAtIndex(getFocusedCell.rowIndex) : null
        getRowNode && gridApi.value?.refreshCells({
          rowNodes: [getRowNode],
          force: true,
        })

        const newCardData = generateCardData(data, this.selectedRanges, configStore.config)
        
        const updatedProperties = {}
        const commonKeys = Object.keys(newCardData).filter((key) => card.hasOwnProperty(key))
        commonKeys.forEach((key) => {
          updatedProperties[key] = newCardData[key]
        })

        Object.assign(card, updatedProperties)

        if (cardId === this.selectedCardData.id) {
          this.selectedCardData = {
            ...this.selectedCardData,
            ...updatedProperties
          }
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateFields' })
      }
    },

    async updateField(value, cardId, field) {
      const configStore = useConfigStore()

      try {
        if (!configStore.config?.hasActiveSubscription) return

        let backendFieldName = field
        if (field === 'attributes') {
          backendFieldName = 'flags'
        }

        const dataForUpdate = {
          [backendFieldName]: value
        }

        if (['type', 'sport', 'year', 'brand', 'set', 'player', 'subset', 'team', 'variation', 'card_number', 'number'].includes(field)) {
          dataForUpdate['collx_match_id'] = null
          dataForUpdate['serial_number'] = null
        }

        if (!cardId) return

        const { data } = await apiClient().patch(`/cards/${cardId}`, dataForUpdate)

        const card = this.cards.find(element => element.id === cardId)

        const getFocusedCell = gridApi.value?.getFocusedCell()
        const getRowNode = getFocusedCell ? gridApi.value?.getDisplayedRowAtIndex(getFocusedCell.rowIndex) : null

        const newCardData = generateCardData({...card, ...data}, this.selectedRanges, configStore.config)
        field !== 'subset' && gridApi.value?.applyTransaction({ update: [newCardData] })

        const updatedProperties = {}

        if (!card || Object.keys(card).length === 0) return

        const commonKeys = Object.keys(newCardData).filter((key) => card.hasOwnProperty(key))
        commonKeys.forEach((key) => {
          updatedProperties[key] = newCardData[key]
        })

        const columnsToRefresh = [field, 'title']
        if (field === 'quantity') {
          columnsToRefresh.push('availableQuantity')
        }

        Object.assign(card, updatedProperties)

        getRowNode && gridApi.value?.refreshCells({
          rowNodes: [getRowNode],
          columns: columnsToRefresh,
          force: true,
          suppressFlash: true
        })

        if (cardId === this.selectedCardData.id) {
          this.selectedCardData = {
            ...this.selectedCardData,
            ...updatedProperties
          }
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateField' })
      }
    },

    async updateBulkSpecificFieldsBatch({ value, field, cardIds }) {
      try {
        await apiClient().post('/cards/bulk-update', {
          cardIds,
          field,
          value
        })
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateBulkSpecificFieldsBatch' })
      }
    },

    /**
     * `skipRefetch` is for callers that fire TWO writes for one user action (a
     * title/description template writes the rendered text AND the template id).
     * Each write used to refetch, un-awaited, so the two refetches raced and the
     * earlier one — holding pre-template rows — could land last. The caller now
     * awaits the intermediate write with `skipRefetch: true` and lets the final
     * one refresh once, after both have landed.
     */
    async updateBulkFieldsBatch({ value, field, unlinkPromptActionTrigger = '', additionalAction = '', isInventory = false, skipRefetch = false }) {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const arrayOfIds = []
      
      let selectedRange = this.selectedRanges ? [ ...this.selectedRanges ] : []

      try {
        let backendFieldName = field || this.selectedColumn
        if (this.selectedColumn === 'attributes') {
          backendFieldName = 'flags'
        }

        if (this.selectedColumn === 'card_number') {
          backendFieldName = 'number'
        }

        if (this.selectedColumn === 'cert_no') {
          backendFieldName = 'certification_number'
        }

        if (this.selectedColumn && this.shouldUpdateColumn) {
          // for column
          selectedRange = this.cards.map((item, index) => {
            return {
              startIndex: index
            }
          })
        }

        for (let i = 0; i < selectedRange.length; i++) {
          if (this.selectedColumn === 'status' && this.cards[selectedRange[i].startIndex || 0].red && value === 'ready_to_list') continue
          if (['grader', 'cert_no', 'grade_number'].includes(this.selectedColumn) && !this.cards[selectedRange[i].startIndex || 0].graded) continue
          if (['condition'].includes(this.selectedColumn) && this.cards[selectedRange[i].startIndex || 0].graded) continue
          // Skip if condition value doesn't belong to card's type
          if (['condition'].includes(this.selectedColumn)) {
            const cardType = this.cards[selectedRange[i].startIndex || 0].type
            const validConditions = ALL_CONDITIONS[cardType] || []
            if (!validConditions.some(c => c.id === value)) continue
          }

          if (backendFieldName === 'collx_match_id') {
            if (this.cards[selectedRange[i].startIndex || 0].subset) {
              arrayOfIds.push(this.cards[selectedRange[i].startIndex || 0].id)
            }
          } else if (backendFieldName === 'subset' && unlinkPromptActionTrigger === 'remove') {
            if (this.cards[selectedRange[i].startIndex || 0].subset) {
              arrayOfIds.push(this.cards[selectedRange[i].startIndex || 0].id)
            }
          } else {
            arrayOfIds.push(selectedRange[i].startIndex ? this.cards[selectedRange[i].startIndex]?.id : this.cards[0]?.id)
          }
        }

        if (this.selectedColumn === 'sku' && this.automaticallyGenerateSku && !additionalAction) {
          await apiClient().post('/cards/re-sku', {
            cardIds: arrayOfIds
          })
        } else {
          await apiClient().post('/cards/bulk-update', {
            cardIds: arrayOfIds,
            field: backendFieldName,
            value
          })
        }


        if (skipRefetch) return
        if (!isInventory) {
          this.fetchSelectedCards()
        } else {
          const inventoryStore = useInventoryStore()
          const userStore = useUserStore()
          inventoryStore.fetchInventory({ id: userStore.user.default_inventory_id, perPage: 25, isPagination: false })
          inventoryStore.fetchMarketPrices({ id: userStore.user.default_inventory_id, perPage: 25, isPagination: false })
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateFieldsBatch' })
      }
    },

    /**
     * Make a row the selected card — the one the Inspector / preview panel reads.
     *
     * Prefers the object already in `cards` so the panel and the grid share one
     * reactive row (an edit in either shows in both); falls back to the row data
     * handed in, which is what the floating expand button carries.
     *
     * Ids are compared as STRINGS: inventory rows can carry `id` as a string
     * while `cards` holds numbers, and a `===` between the two silently found
     * nothing — leaving the panel on whatever card it was showing before.
     */
    selectCard(card) {
      if (!card?.id) return

      this.selectedCardId = card.id
      this.selectedCardData = this.cards.find(item => String(item.id) === String(card.id)) || { ...card }
    },

    async fetchUpdatedCard(cardId) {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      const globalStore = useGlobalStore()
      try {
        const card = this.cards.find(item => item.id === cardId)

        if (!card) {
          return
        }

        const { data } = await apiClient().get(`/cards/${cardId}`)

        if (!data) return

        const newCardData = generateCardData(data, this.selectedRanges, configStore.config)

        newCardData.runAnimation = true

        Object.assign(card, newCardData)

        // `selectedCardData` is a SEPARATE object, not a reference into `cards`
        // — the inspector preview, the image modal and the details fields all
        // read it. Updating only the list is why a rotation showed up in the
        // card list on the left but never in the preview beside it.
        if (this.selectedCardData?.id === cardId) {
          Object.assign(this.selectedCardData, newCardData)
        }

        globalStore.pusherLoading = false
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'fetchUpdatedCard' })
        globalStore.pusherLoading = false
      }
    },

    async updateCardStatus(cardId, status) {
      const configStore = useConfigStore()

      if (!configStore.config?.hasActiveSubscription) return

      try {
        await this.updateField(status, cardId, 'status')
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateCardStatus' })
      }
    },

    async rotateImage(imageTab, degreeOfRotation = 90, cardId = this.selectedCardId) {
      const globalStore = useGlobalStore()
      const splittedTab = imageTab.split('_')
      const tab = splittedTab[0]

      try {
        globalStore.pusherLoading = true
        const { data, response } = await apiClient(true).post(`/cards/${cardId}/rotate-${tab}/${degreeOfRotation}`)

        if (response && !response.data.ok) {
          globalStore.pusherLoading = false
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'rotateImage' })
        globalStore.pusherLoading = false
      }
    },

    async swapImages() {
      const cardIds = []

      try {
        if (this.selectedRanges) {
          for (let i = 0; i < this.selectedRanges.length; i++) {
            cardIds.push(this.cards[this.selectedRanges[i].startIndex].id)
          }
        } else {
          cardIds.push(this.selectedCardId)
        }


        apiClient(true).post('/cards/swap-images', {
          cardIds
        })

      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'swapImages' })
      }
    },

    async replaceImage(formData) {
      const globalStore = useGlobalStore()

      try {
        globalStore.replaceImageLoader = true
        apiClient(true).put(`/cards/${this.selectedCardId}?skip_vision=1`, formData)
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'replaceImage' })
        globalStore.replaceImageLoader = false
      }
    },

    async deleteCards(ids) {
      try {
        let cardIds = []

        if (!ids) {
          for (let i = 0; i < this.selectedRanges.length; i++) {
            cardIds.push(this.selectedRanges[i]?.id)
          }
        } else {
          cardIds = ids
        }

        const { data } = await apiClient().delete('/cards', {
          data: {
            cardIds
          }
        })

        this.selectedRanges = []
        this.selectedCards = {}

        this.cards = this.cards.reduce((acc, item) => {
          if (!data.cardIds.includes(item.id)) {
            acc.push(item)
          }

          return acc
        }, [])
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'deleteCards' })
      }
    },

    async deleteCard(id) {
      try {
        const { data } = await apiClient().delete(`/cards/${this.selectedCardData?.id || id}`)

        if (data?.deleted) {
          this.deleteCardSuccess = true
        } else {
          this.deleteCardError = true
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'deleteCard' })
        this.deleteCardError = true
      }
    },

    async fetchAdditionalSuggestions(card_id, field, query) {
      const results = [
        'Option One',
        'Option Two',
        'Option Three',
        'Option Four',
        'Option Five',
      ]

      try {
        // const { data } = await apiClient().get('/batch/detail/', {
        //   params: {
        //     batch_id: this.selectedBatchId,
        //     card_id
        //     field,
        //     query
        //   }
        // })

        this.fieldSearchResult = results.filter((result) => {
          return  result
                  .toLowerCase()
                  .replace(/\s+/g, '')
                  .includes(query.toLowerCase().replace(/\s+/g, ''))
        })
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'fetchAdditionalSuggestions' })
      }
    },

    async updateMatchedCard(selectedMatch) {
      const configStore = useConfigStore()
      try {
        if (!configStore.config?.hasActiveSubscription) return

        const { data } = await apiClient().post(`/cards/${this.selectedCardId}/update-match`, { ...selectedMatch })

        if (!data) return

        const card = this.cards.find(item => item.id === data.id)

        if (!card) {
          return
        }

        const newCardData = generateCardData(data, this.selectedRanges, configStore.config)
        gridApi.value?.applyTransaction({ update: [newCardData] })


        Object.assign(card, newCardData)

        this.selectedCardData = {
          ...card,
          ...newCardData
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateMatchedCard' })
      }
    },


    async importMatchedCard(selectedMatch) {
      const configStore = useConfigStore()
      const batchStore = useBatchStore()
      try {
        if (!configStore.config?.hasActiveSubscription) return

        const { data } = await apiClient().post(`/batches/${batchStore.selectedBatchId}/import-match`, { ...selectedMatch, sport: selectedMatch.sport === 'NON_SPORT' ? 'Non-Sport' : getProperSport(selectedMatch.sport), type: batchStore.selectedBatch.type })
        this.fetchSelectedCards()

        return data
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'importMatchedCard' })
      }
    },

    async updateCustomFieldsByCardId(body, fieldIndex) {
      const configStore = useConfigStore()
      try {
        if (!configStore.config?.hasActiveSubscription) return

        const customFields = []

        let selectedRange = this.selectedRanges ? [ ...this.selectedRanges ] : []
        
        if (this.selectedColumn && this.shouldUpdateColumn) {
          // for column
          selectedRange = this.cards.map((item, index) => {
            return {
              startIndex: index
            }
          })
        }

        for (let i = 0; i < selectedRange.length; i++) {
          customFields.push(
            {
              ...body,
              card_id: selectedRange[i].startIndex ? this.cards[selectedRange[i].startIndex]?.id : this.cards[0]?.id
            }
          )
        }
        
        const { data } = await apiClient().put(`/cards/custom-fields`, customFields)

        if (data?.ok) { 
          this.cards = this.cards.reduce((acc, card, index) => {
            const selectedIndex = selectedRange.find((item) => item.startIndex === index)

            if (selectedIndex) {
              acc.push({
                ...card,
                [`custom_fields_${fieldIndex}`]: body.value
              })
            } else {
              acc.push(card)
            }

            return acc
          }, [])
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateCustomFieldByCardId' })
      }
    },
    
    async updateCustomFieldByCardId(body, fieldIndex) {
      const configStore = useConfigStore()
      try {
        if (!configStore.config?.hasActiveSubscription) return

        const { data } = await apiClient().put(`/cards/${this.selectedCardId}/custom-field`, body)
        this.selectedCardData[`custom_fields_${fieldIndex}`] = body.value

        this.fetchUpdatedCard(this.selectedCardId)

        this.selectedCardData.customFieldsListForInspector = this.selectedCardData.customFieldsListForInspector.reduce((acc, item) => {
          if (item.id === body.id) {
            acc.push({ ...item, value: body.value })
          } else {
            acc.push(item)
          }

          return acc


        }, [])
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateCustomFieldByCardId' })
      }
    },

    async fetchCardsStatuses() {
      try {
        if (!this.batchCardsBasedOnStatus.processingBatchCards.length) return

        const { data } = await apiClient().get(`batches/${this.selectedBatchId}/cards?status=processing`)

        for(let i = 0; i < this.batchCardsBasedOnStatus.processingBatchCards.length; i++) {
          const isStillProcessing =  data ? data.items.find(item => item.id === this.batchCardsBasedOnStatus.processingBatchCards[i].id) : false
          if (!isStillProcessing) {
            await this.fetchUpdatedCard(this.batchCardsBasedOnStatus.processingBatchCards[i].id)
          }
        }

      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'fetchCardsStatuses' })
      }
    },

    async fetchSubset(parentId, collxMatchId) {
      const batchStore = useBatchStore()
      try {
        if (batchStore.selectedBatchDetailsViewOption !== 'Inspector' && !batchStore.openedPanel) {
          this.fieldSuggestions = []
        }

        this.loadingSuggestions = true
        const { data } = await apiClient().get(`suggestions/subsets?parent_set_id=${parentId}&collx_match_id=${collxMatchId}`)

        const baseCard = []
        const cards = []

        if (data.matches.length === 1 && !data.matches[0].set.parent && !data.matches[0].set.subset) {
          this.fieldSuggestions = []
          this.loadingSuggestions = false
          return
        }

        for(let i = 0; i < data.matches.length; i++) {
          if (!data.matches[i].set.parent) {
            baseCard.push(data.matches[i])
          } else {
            cards.push({ ...data.matches[i] })
          }
        }

        this.fieldSuggestions = [...baseCard.sort(sortSubsetSuggestions), ...cards.sort(sortSubsetSuggestions)]
        this.loadingSuggestions = false
      } catch (error) {
        this.loadingSuggestions = false
        handleApiError(error, { component: 'CardStore', action: 'fetchSubset' })
      }
    },

    updateCardsFromSuccessfulData(successfulCards, eventType) {
      const configStore = useConfigStore()

      // Prepare updated cards list
      const updatedCards = this.cards.map(existingCard => {
        // Find if this card was updated in successful cards
        const updatedCard = successfulCards.find(successfulCard => successfulCard.id === existingCard.id)

        if (updatedCard) {
          if (eventType === 'collx_listing_completed') {
            return {
              ...existingCard,
              collxListedStatus: updatedCard?.listing?.collx_status,
              listing: updatedCard?.listing
            }
          }

          // Merge existing card with successful data
          const newCardData = generateCardData(updatedCard, this.selectedRanges, configStore.config)
          return {
            ...existingCard,
            whatnotListedStatus: newCardData.whatnotListedStatus,
            ebayListedStatus: newCardData.ebayListedStatus,
            collxListedStatus: newCardData.collxListedStatus,
            shopifyListedStatus: newCardData.shopifyListedStatus,
            listingId: newCardData.listingId,
            sku: newCardData.sku,
            availableQuantity: newCardData.availableQuantity,
            listing: newCardData.listing
          }
        }

        // Return existing card unchanged
        return existingCard
      })

      // Replace the entire cards array with updated cards
      this.cards = updatedCards
      
      gridApi.value?.refreshCells({
        force: true,
        suppressFlash: true,
      })

      // Update selectedCardData if it matches one of the successful cards
      if (this.selectedCardData && this.selectedCardData.id) {
        const updatedCard = successfulCards.find(card => card.id === this.selectedCardData.id)

        if (eventType === 'collx_listing_completed') {
          this.selectedCardData = {
            ...this.selectedCardData,
            collxListedStatus: updatedCard?.listing?.collx_status,
            listing: updatedCard?.listing
          }

          return
        }

        if (updatedCard) {
          const newCardData = generateCardData(updatedCard, this.selectedRanges, configStore.config)
          this.selectedCardData = {
            ...this.selectedCardData,
            whatnotListedStatus: newCardData.whatnotListedStatus,
            ebayListedStatus: newCardData.ebayListedStatus,
            collxListedStatus: newCardData.collxListedStatus,
            shopifyListedStatus: newCardData.shopifyListedStatus,
            listingId: newCardData.listingId,
            sku: newCardData.sku,
            availableQuantity: newCardData.availableQuantity,
            listing: newCardData.listing
          }
        }
      }
    },

    async resetInventory(type) {
      try {
        if (type === 'inventory') {
          await apiClient().post('/cards/clear-inventory-listings')
        } else if (type === 'inventoryAndBatches') {
          await apiClient().post('/cards/clear-all-data')
        }

        this.resetProcessingStarted = true
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'resetInventory' })
      }
    },

    /**
     * Rebuild `bulkSalesValues` from the CURRENT pricing configs + the CURRENT
     * card rows, and return them.
     *
     * `bulkSalesValues` used to be produced only by a watcher inside
     * BulkSalePrice.vue. That watcher fires on CHANGES, so repeating an
     * adjustment the popover was already configured with (pick "$", type 0.07
     * when it already said "$ 0.07") changed nothing, no recompute happened,
     * and Update re-POSTed the previous — already applied — values, i.e. the
     * price appeared not to move. Submitting now recomputes through here so the
     * numbers sent always match what the popover shows.
     */
    recalculateBulkSalesValues() {
      const configs = this.bulkPricingConfigs || { ...BULK_PRICING_CONFIGS }

      const cards = Object.keys(this.selectedCards).length
        ? Object.values(this.selectedCards)
        : this.cards

      // "Apply Market Price" / "Apply Purchase Price" with no adjustment typed:
      // the price IS the source value, no formula involved.
      if (this.anotherPriceSource && !Number(configs.amount_to_price)) {
        this.bulkSalesValues = getBulkSalesValues({
          cards,
          useSuggestedPrice: this.anotherPriceSource === 'suggested',
          usePurchasePrice: this.anotherPriceSource === 'purchase',
        })

        return this.bulkSalesValues
      }

      // Nothing configured that could move a price (no amount, no rounding, no
      // minimum, no new value typed) => nothing to submit. Keeps the Update
      // button disabled instead of letting it POST the prices back unchanged
      // and report "Sales price updated".
      const hasAdjustment = !!Number(configs.amount_to_price)
        || !!configs.round_to_nearest
        || !!configs.add_minimal_price
        || !!this.currentBulkValue.start_price
        || !!this.anotherPriceSource

      if (!hasAdjustment) {
        this.bulkSalesValues = []

        return this.bulkSalesValues
      }

      const options = {
        cards,
        selectedCalculation: configs.price_adjustment_type === CALCULATION_TYPES[1] ? CALCULATION_TYPES[1] : CALCULATION_TYPES[0],
        selectedMethod: configs.calculated_method ? CALCULATION_METHODS[0] : CALCULATION_METHODS[1],
        calculatedPriceValue: configs.amount_to_price,
        roundToNearestEnabled: !!configs.round_to_nearest,
        roundToNearestType: configs.round_to_nearest_type || ROUNDING_OPTIONS[0],
        minimalPriceEnabled: !!configs.add_minimal_price,
        minimalPriceValue: configs.minimal_price ?? 0,
      }

      if (this.currentBulkValue.start_price) {
        if (!Number(configs.amount_to_price)) {
          options.ignoreAdjustments = true
        }

        this.bulkSalesValues = getBulkSalesValues(options, this.currentBulkValue.start_price)
      } else {
        this.bulkSalesValues = getBulkSalesValues(options)
      }

      return this.bulkSalesValues
    },

    // Back to a clean slate after a sales-price bulk edit is applied, so the
    // next one starts from the defaults instead of silently reusing the
    // previous run's configs and already-submitted values.
    resetBulkPricingState() {
      this.bulkSalesValues = []
      this.bulkPricingConfigs = { ...BULK_PRICING_CONFIGS }
      this.anotherPriceSource = false
      this.currentBulkValue = { ...this.currentBulkValue, start_price: '' }
    },

    async updateBulkSalesValues({ isInventory = false }) {
      // Snapshot the payload: the caller clears the pricing state as soon as
      // the request is on its way, and the refetch below must not race it.
      const cardsToUpdate = [...this.bulkSalesValues]

      try {
        await apiClient().post('/cards/bulk-update-sales', {
          Cards: cardsToUpdate,
          field: 'start_price',
        })

        if (!isInventory) {
          this.fetchSelectedCards()
        } else {
          const inventoryStore = useInventoryStore()
          const userStore = useUserStore()
          inventoryStore.fetchInventory({ id: userStore.user.default_inventory_id, perPage: 25, isPagination: false })
          inventoryStore.fetchMarketPrices({ id: userStore.user.default_inventory_id, perPage: 25, isPagination: false })
        }
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateBulkSalesValues' })
      }
    },

    async updateForSaleQuantity(quantity, cardId = this.selectedCardData.id) {
      try {
        await apiClient().put(`/cards/${this.selectedCardData?.id || cardId}/quantity-available`, {
          quantity_available: quantity,
        })  

        this.selectedCardData.availableQuantity = quantity
      } catch (error) {
        handleApiError(error, { component: 'CardStore', action: 'updateForSaleQuantity' })
      }
    }
  }
})
